import { html, css } from 'lit';
const CLS = {
    key: 'j-key',
    str: 'j-str',
    num: 'j-num',
    lit: 'j-lit',
    punct: 'j-punct',
    plain: 'j-plain',
};
/**
 * Tokenise pretty-printed JSON.
 *
 * Works on the FORMATTED text rather than on the parsed value, so the indentation, key order and
 * line breaks are exactly what `JSON.stringify` produced — one thing deciding layout, one thing
 * deciding colour. A key is a string followed by a colon; that is the only lookahead needed.
 */
export function tokenize(pretty) {
    const out = [];
    let i = 0;
    const push = (cls, text) => {
        if (!text)
            return;
        const last = out[out.length - 1];
        if (last && last.cls === cls)
            last.text += text;
        else
            out.push({ cls, text });
    };
    while (i < pretty.length) {
        const c = pretty[i];
        if (c === '"') {
            // Scan the whole string literal, honouring escapes — a value containing \" must not end it.
            let j = i + 1;
            while (j < pretty.length) {
                if (pretty[j] === '\\')
                    j += 2;
                else if (pretty[j] === '"')
                    break;
                else
                    j++;
            }
            const lit = pretty.slice(i, Math.min(j + 1, pretty.length));
            // A colon after optional whitespace ⇒ this string is a key.
            const rest = pretty.slice(j + 1);
            push(/^\s*:/.test(rest) ? CLS.key : CLS.str, lit);
            i = j + 1;
            continue;
        }
        if (/[-\d]/.test(c) && /[-\d]/.test(pretty[i - 1] ?? ' ') === false) {
            const m = /^-?\d+(\.\d+)?([eE][+-]?\d+)?/.exec(pretty.slice(i));
            if (m) {
                push(CLS.num, m[0]);
                i += m[0].length;
                continue;
            }
        }
        const word = /^(true|false|null)\b/.exec(pretty.slice(i));
        if (word) {
            push(CLS.lit, word[0]);
            i += word[0].length;
            continue;
        }
        push(/[{}[\],:]/.test(c) ? CLS.punct : CLS.plain, c);
        i++;
    }
    return out;
}
/** Pretty-print if it parses; otherwise hand back what we were given rather than hiding it. */
export function pretty(raw) {
    if (!raw)
        return { text: '', json: false };
    try {
        return { text: JSON.stringify(JSON.parse(raw), null, 2), json: true };
    }
    catch {
        return { text: raw, json: false };
    }
}
/**
 * A labelled JSON section of a card.
 *
 * FLAT, not foldable. Folding was tried and removed: a payload split into "5 keys" placeholders
 * reads worse than the payload — you cannot scan it, you cannot copy it in one go, and the controls
 * add a column of chrome to every line that has one. A short result wants to be read, and a long one
 * scrolls, which is a thing everybody already knows how to do.
 *
 * `absent` is what a missing value reads as, and it takes the LABEL'S place rather than sitting
 * beside it: "ARGUMENTS  No arguments" says the same thing twice in two type styles.
 */
export function renderJson(label, raw, absent) {
    const { text, json } = pretty(raw ?? '');
    if (!text || text === '{}')
        return html `<div class="j-wrap"><span class="j-label wide">${absent ?? label}</span></div>`;
    return html `<div class="j-wrap">
    <span class="j-label">${label}</span>
    <pre class="j-pre">${json ? tokenize(text).map((tk) => html `<span class=${tk.cls}>${tk.text}</span>`) : text}</pre>
  </div>`;
}
/**
 * THE SAME COLOURS, FOR A SURFACE THAT IS NOT THE TRANSCRIPT.
 *
 * The token classes are declared beside the tool rows that first needed them, inside a shadow root
 * nothing else can reach. The artifact viewer shows the same kind of thing — a payload somebody is
 * checking — in a shadow root of its own, and a second set of values there would be the drift the
 * shared tokeniser exists to prevent. One sheet, adopted wherever the tokens are drawn.
 */
export const codeTokenCss = css `
  .j-key {
    color: var(--ai);
  }
  .j-str {
    color: var(--green, #3fb950);
  }
  .j-num {
    color: var(--purple, #a371f7);
  }
  .j-lit {
    color: var(--yellow, #d29922);
  }
  .j-punct {
    color: var(--text-tertiary);
  }
  .j-plain {
    color: var(--text-secondary);
  }
  .j-com {
    color: var(--text-tertiary);
    font-style: italic;
  }
`;
/** The words a reader scans a module for. Not a parser's keyword set — a reader's. */
const JS_WORDS = /^(const|let|var|function|return|if|else|for|of|in|while|switch|case|break|continue|new|class|extends|import|from|export|default|async|await|try|catch|finally|throw|typeof|instanceof|delete|void|yield|this|super|true|false|null|undefined)\b/;
/**
 * Tokenise a JavaScript module for READING.
 *
 * The same shape as the JSON tokeniser above and for the same reason: the viewer's Source tab was a
 * plain text node, so a widget's program — the thing a reader opens that tab to check — was one
 * colour from end to end. This is a scanner, not a parser: strings (all three quotes, escapes
 * honoured), comments, numbers, keywords and property-ish names. It cannot tell a regex from a
 * division and does not try; nothing downstream depends on it being right, because the only thing
 * it decides is what colour a run of characters is drawn in.
 */
export function tokenizeJs(src) {
    const out = [];
    const push = (cls, text) => {
        if (!text)
            return;
        const last = out[out.length - 1];
        if (last && last.cls === cls)
            last.text += text;
        else
            out.push({ cls, text });
    };
    let i = 0;
    while (i < src.length) {
        const c = src[i];
        const two = src.slice(i, i + 2);
        if (two === '//') {
            const end = src.indexOf('\n', i);
            push('j-com', src.slice(i, end < 0 ? src.length : end));
            i = end < 0 ? src.length : end;
            continue;
        }
        if (two === '/*') {
            const end = src.indexOf('*/', i + 2);
            push('j-com', src.slice(i, end < 0 ? src.length : end + 2));
            i = end < 0 ? src.length : end + 2;
            continue;
        }
        if (c === '"' || c === "'" || c === '`') {
            let j = i + 1;
            while (j < src.length) {
                if (src[j] === '\\')
                    j += 2;
                else if (src[j] === c)
                    break;
                else
                    j++;
            }
            push(CLS.str, src.slice(i, Math.min(j + 1, src.length)));
            i = j + 1;
            continue;
        }
        if (/\d/.test(c) && !/[\w$]/.test(src[i - 1] ?? ' ')) {
            const m = /^0[xXbBoO][\da-fA-F_]+|^\d[\d_]*(\.\d[\d_]*)?([eE][+-]?\d+)?/.exec(src.slice(i));
            if (m) {
                push(CLS.num, m[0]);
                i += m[0].length;
                continue;
            }
        }
        if (/[A-Za-z_$]/.test(c)) {
            const word = /^[A-Za-z_$][\w$]*/.exec(src.slice(i));
            const kw = JS_WORDS.exec(word[0]);
            // A name followed by "(" reads as something being CALLED, which is what a reader is looking
            // for in a module they did not write.
            const called = /^\s*\(/.test(src.slice(i + word[0].length));
            push(kw ? CLS.lit : called ? CLS.key : CLS.plain, word[0]);
            i += word[0].length;
            continue;
        }
        push(/[{}[\](),;:.]/.test(c) ? CLS.punct : CLS.plain, c);
        i++;
    }
    return out;
}
/**
 * A document, tokenised by what it IS.
 *
 * JSON is pretty-printed first — a declaration arriving as one long line is unreadable, and the
 * whole point of the tab is to read it. Anything else is handed back as written.
 */
export function tokenizeDoc(text, lang) {
    if (lang === 'json') {
        const { text: formatted } = pretty(text);
        return { text: formatted, tokens: tokenize(formatted) };
    }
    if (lang === 'js' || lang === 'ts')
        return { text, tokens: tokenizeJs(text) };
    return { text, tokens: [{ cls: CLS.plain, text }] };
}
//# sourceMappingURL=json-view.js.map